import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// The overview's measures each on a row of their own: a strip of daily bars
// per measure, every strip on the same day axis and each on its own scale,
// labelled with its total, in the overview's colors. A measure with a `part`
// (the moments, and the pushed among them) draws the part solid at the foot
// of the bar and the rest faded, as the overview does. Each strip names its
// measure, so it needs no legend; every value is in the day-by-day table too.
// A click on a day opens it, as on the overview.

const STRIP_HEIGHT = 40
// A day with any of the measure keeps at least this much bar, so a one among
// tens still shows; an empty day has none.
const MIN_BAR_PX = 2

const dayLabel = (day) => dayjs(day).format('ddd MMM D')
const barHeight = (value, max) =>
  value ? Math.max(MIN_BAR_PX, Math.round((value / max) * STRIP_HEIGHT)) : 0

const Strip = ({ days, series, color, faded, selectedDay, onSelectDay }) => {
  const max = Math.max(0, ...days.map((day) => day[series.key] || 0))
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        height: STRIP_HEIGHT,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {days.map((day) => {
        const value = day[series.key] || 0
        const part = series.part ? day[series.part.key] || 0 : value
        const height = barHeight(value, max)
        const partHeight = series.part ? barHeight(part, max) : height
        return (
          <Tooltip
            key={day.day}
            disableInteractive
            placement="top"
            title={
              <Box sx={{ fontSize: 12 }}>
                <b>{series.format(value)}</b>
                {series.part && ` · ${part} ${series.part.label}`}
                <Box sx={{ opacity: 0.8 }}>{dayLabel(day.day)}</Box>
              </Box>
            }
          >
            {/* The whole day column is the hover target, not the bar. */}
            <Box
              onClick={() => onSelectDay?.(day.day)}
              sx={{
                flex: 1,
                minWidth: 0,
                px: '1px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                cursor: 'pointer',
                bgcolor: day.day === selectedDay ? 'action.selected' : null,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {height > partHeight && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 24,
                    height: height - partHeight,
                    bgcolor: faded,
                    borderRadius: '4px 4px 0 0',
                  }}
                />
              )}
              {partHeight > 0 && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 24,
                    height: partHeight,
                    bgcolor: color,
                    borderRadius: height > partHeight ? 0 : '4px 4px 0 0',
                  }}
                />
              )}
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

const ActivityStrips = ({ days, series, colors, selectedDay, onSelectDay }) => {
  const middle = days[Math.floor(days.length / 2)]
  const total = (key) => days.reduce((sum, day) => sum + (day[key] || 0), 0)
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '150px minmax(0, 1fr)',
        columnGap: 1.5,
        rowGap: 1,
        alignItems: 'end',
      }}
    >
      {series.map((entry) => (
        <Box key={entry.key} sx={{ display: 'contents' }}>
          <Box sx={{ pb: 0.25 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
              {entry.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.format(total(entry.key))}
              {entry.part && ` · ${total(entry.part.key)} ${entry.part.label}`}
            </Typography>
          </Box>
          <Strip
            days={days}
            series={entry}
            color={colors[entry.color]}
            faded={colors.unpushed}
            selectedDay={selectedDay}
            onSelectDay={onSelectDay}
          />
        </Box>
      ))}
      <Box />
      {days.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            color: 'text.secondary',
            fontSize: 11,
          }}
        >
          <span>{dayjs(days[0].day).format('MMM D')}</span>
          <span>{dayjs(middle.day).format('MMM D')}</span>
          <span>{dayjs(days[days.length - 1].day).format('MMM D')}</span>
        </Box>
      )}
    </Box>
  )
}

export default ActivityStrips
